import { useState, useRef } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from 'lucide-react';

import { useGetSuggestedICD10Codes } from '@/hooks/api/use-get-suggested-icd-10-codes';
import { useGradeFlow } from '@/states/grade-flow';

import { Label } from '@/components/form/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OptionButton } from '@/components/form/option-button';

export function ICDCodesSelect() {
  const { t } = useTranslation();
  const { data = [] } = useGetSuggestedICD10Codes();
  const { isDisabled, form, setSelectedCodes, setSelectedCode } = useGradeFlow(
    useShallow((s) => ({
      isDisabled: s.form.is_readable === undefined || s.isSavingGrade,
      form: s.form,
      setSelectedCodes: s.setICDCodesSelected,
      setSelectedCode: s.setICDCodeSelected,
    }))
  );

  const [searchValue, setSearchValue] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);

  // filter client-side
  const filtered = searchValue
    ? data.filter((code) => code.toLowerCase().includes(searchValue.toLowerCase()))
    : data;

  const handleToggle = (code: string) => {
    const selected = form.icd_10_codes ?? [];
    if (selected.includes(code)) {
      setSelectedCodes(selected.filter((c) => c !== code));
    } else {
      setSelectedCodes([...selected, code]);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative max-h-80 w-[576px] flex flex-col items-center gap-3">
        <Label htmlFor="search-icd" className="justify-center">
          {t('grade-question-icd-search')}
        </Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              ref={buttonRef}
              type="button"
              disabled={isDisabled}
              variant="outline-muted"
              className="justify-between w-96"
            >
              <span className="truncate">{t('grade-question-icd-search')}</span>
              <SearchIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[576px] max-h-96 overflow-y-auto p-0"
            style={{ maxHeight: 384 }}
            side="bottom"
          >
            <div className="sticky top-0 z-10 bg-white p-2 border-b">
              <Input
                id="search-icd"
                name="search-icd"
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={t('search')}
                autoComplete="off"
                disabled={isDisabled}
              />
            </div>
            <div className="max-h-80 overflow-y-auto flex flex-col gap-1 p-2">
              {filtered.length > 0 ? (
                filtered.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option}
                    checked={form.icd_10_codes?.includes(option)}
                    onCheckedChange={() => handleToggle(option)}
                    disabled={isDisabled}
                    className="w-full"
                  >
                    {option}
                  </DropdownMenuCheckboxItem>
                ))
              ) : (
                <div className="p-2 text-sm text-gray-500">{t('no-results')}</div>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {form.icd_10_codes?.map((code) => (
          <OptionButton
            active
            key={code}
            value={code}
            className="max-w-md"
            onClick={setSelectedCode}
          >
            {code}
          </OptionButton>
        ))}
      </div>
    </div>
  );
}
